import { createRoot } from "react-dom/client";
import { setAuthTokenGetter, setBaseUrl } from "@workspace/api-client-react";
import App from "./App";
import "./index.css";
import { getValidAccessToken } from "./lib/auth-session";
import { ensureDemoSession } from "./lib/demo-session";
import { getApiOrigin } from "./lib/api-base";
import { reportCrash } from "./lib/crash-report";
import { completeGoogleCalendarConnect } from "./lib/calendar-oauth-complete";
import { ErrorBoundary } from "./components/error-boundary";

// Local demo against the mock API only (`demo-frontend` launch config); stripped from production builds.
if (import.meta.env.DEV && import.meta.env.VITE_DEMO_LOGIN === "1") ensureDemoSession();

const apiOrigin = getApiOrigin();
if (apiOrigin) {
  setBaseUrl(apiOrigin);
}

setAuthTokenGetter(() => getValidAccessToken());

window.addEventListener("error", (event) => {
  reportCrash({
    errorType: event.error?.name || "Error",
    errorMessage: event.message,
    stackTrace: event.error?.stack,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  reportCrash({
    errorType: reason?.name || "UnhandledRejection",
    errorMessage: reason?.message ? String(reason.message) : String(reason),
    stackTrace: reason?.stack,
  });
});

// Finishes a Google Calendar connection if this load is Google's redirect landing back on
// /momentum or /settings with #calendar_completion=... — a no-op on every other page load.
// Render waits for it: the calendar pages read `?calendar=` and the connection once on
// mount, so rendering first showed "Connect" until a refresh. Capped so a hung request
// can't hold the page blank.
const calendarConnectDone = Promise.race([
  completeGoogleCalendarConnect(),
  new Promise<void>((resolve) => window.setTimeout(resolve, 10_000)),
]);

void calendarConnectDone.finally(() => {
  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>,
  );
});
